// ============================================================
// src/App.tsx (replace existing contents)
// ============================================================
import React from "react";
import theme from "./theme";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CssBaseline, ThemeProvider} from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LandingPage from "./features/landing/LandingPage";
import Register from "./features/auth/Register";
import Login from "./features/auth/Login";
import Dashboard from "./features/dashboard/Dashboard";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import OnboardingWizard from "./features/onboarding/OnboardingWizard";
import RecipeDetailScreen from "./features/recipes/RecipeDetailScreen";
import RecipeBrowseScreen from "./features/recipes/RecipeBrowseScreen";
import CookingMode from "./features/recipes/CookingMode";
import ReviewPlan from "./features/mealPlans/ReviewPlan";
import ShoppingList from "./features/shopping/ShoppingList";
import AccountSettings from "./features/account/AccountSettings";
import Membership from "./features/membership/Membership";
import VerifyEmailChange from "./features/account/VerifyEmailChange";
import VerifyAccountDeletion from "./features/account/VerifyAccountDeletion";
import UserPreferences from "./features/preferences/UserPreferences";
import FamilyMembers from "./features/family/FamilyMembers";
import Navigation from "./components/Navigation";
import ForgotPassword from "./features/auth/ForgotPassword";
import ResetPassword from "./features/auth/ResetPassword";
import TermsOfService from "./features/legal/TermsOfService";
import PrivacyPolicy from "./features/legal/PrivacyPolicy";
import DeleteData from "./features/legal/DeleteData";
import ContactUs from "./features/support/ContactUs";
import {
  AdminLayout,
  AdminDashboard,
  RecipeList as AdminRecipeList,
  RecipeEditor as AdminRecipeEditor,
  RecipeCreator as AdminRecipeCreator,
  IngredientList as AdminIngredientList,
  UserList as AdminUserList,
  UserDetail as AdminUserDetail,
  DuplicatesList as AdminDuplicatesList,
  DemoMealsEditor as AdminDemoMealsEditor,
  PushNotifications as AdminPushNotifications,
  CreatorList as AdminCreatorList,
  CreatorDetail as AdminCreatorDetail,
  ChefList as AdminChefList,
  ChefDetail as AdminChefDetail,
  PromoCodeList as AdminPromoCodeList,
  PayoutQueue as AdminPayoutQueue,
  ChurnSurveys as AdminChurnSurveys,
  RecipeEnrichment as AdminRecipeEnrichment,
  RecipeSeeding as AdminRecipeSeeding,
  JobsSync as AdminJobsSync,
  ImageTest as AdminImageTest,
  ImageBackfill as AdminImageBackfill,
} from "./features/admin";
import { CreatorDashboard, CreatorAgreement } from "./features/creator";
import { ChefDashboard, ChefAgreement } from "./features/chef";
import TrialSurvey from "./features/survey/TrialSurvey";

// Google OAuth Client ID (Web)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Still loading auth state? Show nothing (or could show a spinner)
  if (loading) return null;

  // Not logged in → redirect to login
  if (!user) return <Navigate to="/login" replace />;

  // Logged-in but not onboarded
  if (!user.profileComplete) {
    // Allow access to onboarding and creator agreement (creators set password before onboarding)
    if (location.pathname.startsWith("/onboarding") || location.pathname === "/creator-agreement" || location.pathname === "/chef-agreement") {
      return children;
    }
    // Otherwise push to wizard
    return <Navigate to="/onboarding/plan" replace />;
  }

  // Freemium: onboarded user with no membership row must choose a plan first
  // (Free, Monthly, or Yearly). Prevent them from escaping the paywall.
  if (!user.membership && location.pathname !== "/membership") {
    return <Navigate to="/membership" replace />;
  }

  // Membership check - allow some routes without active membership
  // Paywall triggers when user tries to approve a meal plan
  const allowedWithoutMembership = [
    "/membership",
    "/account-settings",
    "/contact",
    "/dashboard",
    "/recipes",     // Recipe browse — free tier feature
    "/meal-plans",  // Includes /meal-plans/:id/review
    "/meal",        // Recipe detail /meal/:id
    "/shopping-list", // Shopping list — free tier feature
    "/creator-dashboard",  // Creators have free membership, but allow access anyway
    "/creator-agreement",  // Agreement page before dashboard access
    "/chef-dashboard",     // Chefs have free membership, but allow access anyway
    "/chef-agreement",     // Agreement page before dashboard access
  ];
  const isAllowedRoute = allowedWithoutMembership.some(
    (path) => location.pathname === path || location.pathname.startsWith(path + "/")
  );
  if (!user.membershipActive && !isAllowedRoute) {
    return <Navigate to="/membership" replace />;
  }

  // Fully onboarded with active membership → allow the requested page
  return children;
};

const PrivateRouteWithNav: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  return (
    <PrivateRoute>
      <>
        <Navigation />
        {children}
      </>
    </PrivateRoute>
  );
};

// Admin-only route wrapper
const PrivateAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/dashboard" replace />;

  return children;
};

const App: React.FC = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmailChange />} />
          <Route path="/verify-deletion" element={<VerifyAccountDeletion />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/delete-data" element={<DeleteData />} />
          <Route path="/trial-survey" element={<TrialSurvey />} />

          {/* Onboarding - no navigation bar */}
          <Route path="/onboarding/*" element={<PrivateRoute><OnboardingWizard /></PrivateRoute>} />

          {/* Protected routes with navigation bar */}
          <Route path="/dashboard" element={<PrivateRouteWithNav><Dashboard /></PrivateRouteWithNav>} />
          <Route path="/account-settings" element={<PrivateRouteWithNav><AccountSettings /></PrivateRouteWithNav>} />
          <Route path="/membership" element={<PrivateRouteWithNav><Membership /></PrivateRouteWithNav>} />
          <Route path="/user-preferences" element={<PrivateRouteWithNav><UserPreferences /></PrivateRouteWithNav>} />
          <Route path="/family-members" element={<PrivateRouteWithNav><FamilyMembers /></PrivateRouteWithNav>} />
          <Route path="/recipes" element={<PrivateRouteWithNav><RecipeBrowseScreen /></PrivateRouteWithNav>} />
          <Route path="/meal/:id" element={<PrivateRouteWithNav><RecipeDetailScreen /></PrivateRouteWithNav>} />
          <Route path="/recipe/:id/cook" element={<PrivateRouteWithNav><CookingMode /></PrivateRouteWithNav>} />
          <Route path="/meal-plans/:id/review" element={<PrivateRouteWithNav><ReviewPlan /></PrivateRouteWithNav>} />
          <Route path="/shopping-list/:planId" element={<PrivateRouteWithNav><ShoppingList /></PrivateRouteWithNav>} />
          <Route path="/contact" element={<PrivateRouteWithNav><ContactUs /></PrivateRouteWithNav>} />
          <Route path="/creator-dashboard" element={<PrivateRouteWithNav><CreatorDashboard /></PrivateRouteWithNav>} />
          <Route path="/creator-agreement" element={<PrivateRouteWithNav><CreatorAgreement /></PrivateRouteWithNav>} />
          <Route path="/chef-dashboard" element={<PrivateRouteWithNav><ChefDashboard /></PrivateRouteWithNav>} />
          <Route path="/chef-agreement" element={<PrivateRouteWithNav><ChefAgreement /></PrivateRouteWithNav>} />

          {/* Admin routes - no regular nav, uses AdminLayout */}
          <Route path="/admin" element={<PrivateAdminRoute><AdminLayout /></PrivateAdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="recipes" element={<AdminRecipeList />} />
            <Route path="recipes/new" element={<AdminRecipeCreator />} />
            <Route path="recipes/:id" element={<AdminRecipeEditor />} />
            <Route path="ingredients" element={<AdminIngredientList />} />
            <Route path="users" element={<AdminUserList />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="creators" element={<AdminCreatorList />} />
            <Route path="creators/:id" element={<AdminCreatorDetail />} />
            <Route path="chefs" element={<AdminChefList />} />
            <Route path="chefs/:id" element={<AdminChefDetail />} />
            <Route path="promo-codes" element={<AdminPromoCodeList />} />
            <Route path="payouts" element={<AdminPayoutQueue />} />
            <Route path="duplicates" element={<AdminDuplicatesList />} />
            <Route path="enrichment" element={<AdminRecipeEnrichment />} />
            <Route path="seeding" element={<AdminRecipeSeeding />} />
            <Route path="demo-meals" element={<AdminDemoMealsEditor />} />
            <Route path="notifications" element={<AdminPushNotifications />} />
            <Route path="churn-surveys" element={<AdminChurnSurveys />} />
            <Route path="jobs" element={<AdminJobsSync />} />
            <Route path="image-test" element={<AdminImageTest />} />
            <Route path="image-backfill" element={<AdminImageBackfill />} />
          </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  </GoogleOAuthProvider>
);

export default App;